import { AlertCircle, Check, Copy, ExternalLink, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Badge } from "@/common/ui/badge";
import { Button } from "@/common/ui/button";
import { Skeleton } from "@/common/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/common/ui/tabs";
import { copyText } from "@/lib/clipboard";

import { MessageHtmlFrame } from "./MessageHtmlFrame";
import { fetchRawMime, type FetchRawMimeResult, type Message } from "./messages-api";
import { useMessageDetail } from "./use-message-detail";

function formatAddresses(addrs: { email: string; name: string | null }[]): string {
  if (addrs.length === 0) return "—";
  return addrs.map((a) => (a.name ? `${a.name} <${a.email}>` : a.email)).join(", ");
}

type VerdictTone = "pass" | "fail" | "neutral" | "unknown";

function verdictTone(raw: string | undefined): VerdictTone {
  if (!raw) return "unknown";
  const v = raw.toLowerCase();
  if (v === "pass") return "pass";
  if (v === "fail") return "fail";
  return "neutral";
}

function VerdictBadge({
  name,
  verdict,
}: {
  name: string;
  verdict: string | undefined;
}): React.ReactElement {
  const tone = verdictTone(verdict);
  const variant: "default" | "destructive" | "secondary" | "outline" =
    tone === "pass" ? "default" : tone === "fail" ? "destructive" : "secondary";
  const label = verdict ? verdict.toLowerCase() : "—";
  return (
    <Badge variant={variant} data-testid={`verdict-badge-${name}`} className="font-mono">
      {name}: {label}
    </Badge>
  );
}

type CopyState = "idle" | "copied" | "failed";

function CopyButton({
  value,
  testId,
  label,
}: {
  value: string;
  testId: string;
  label: string;
}): React.ReactElement {
  const [state, setState] = useState<CopyState>("idle");
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== undefined) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  const flash = (next: "copied" | "failed"): void => {
    if (timeoutRef.current !== undefined) window.clearTimeout(timeoutRef.current);
    setState(next);
    timeoutRef.current = window.setTimeout(() => setState("idle"), 1500);
  };

  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      onClick={async () => {
        const ok = await copyText(value);
        flash(ok ? "copied" : "failed");
      }}
      data-testid={testId}
      aria-label={label}
      className="h-7 gap-1 px-2"
    >
      {state === "copied" ? (
        <Check className="h-3 w-3" />
      ) : state === "failed" ? (
        <AlertCircle className="h-3 w-3" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
      <span className="text-xs">
        {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : "Copy"}
      </span>
    </Button>
  );
}

// Allowlist http(s) only — `javascript:`, `data:`, `file:`, etc. render as
// plain text so a hostile test email can't ship a clickable XSS into the
// ops-console origin. Links remain copyable.
function isClickableLink(href: string): boolean {
  try {
    const url = new URL(href);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function HeaderPanel({ message }: { message: Message }): React.ReactElement {
  const received = new Date(message.receivedAt);
  return (
    <div className="space-y-4 rounded-md border bg-card p-4">
      <div>
        <h2 className="text-xl font-semibold">
          {message.subject ?? <span className="italic text-muted-foreground">(no subject)</span>}
        </h2>
      </div>
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[max-content_1fr]">
        <dt className="font-medium text-muted-foreground">From</dt>
        <dd data-testid="header-from" className="break-all">
          {formatAddresses(message.from)}
        </dd>
        <dt className="font-medium text-muted-foreground">To</dt>
        <dd data-testid="header-to" className="break-all">
          {formatAddresses(message.to)}
        </dd>
        <dt className="font-medium text-muted-foreground">Received</dt>
        <dd data-testid="header-received-at">{received.toISOString()}</dd>
        <dt className="font-medium text-muted-foreground">Namespace</dt>
        <dd data-testid="header-namespace" className="break-all font-mono text-xs">
          {message.namespaceId}
        </dd>
        <dt className="font-medium text-muted-foreground">SES ID</dt>
        <dd data-testid="header-ses-id" className="break-all font-mono text-xs">
          {message.sesMessageId}
        </dd>
      </dl>
      <div className="flex flex-wrap gap-2">
        <VerdictBadge name="DKIM" verdict={message.envelope.dkim} />
        <VerdictBadge name="SPF" verdict={message.envelope.spf} />
        <VerdictBadge name="DMARC" verdict={message.envelope.dmarc} />
      </div>
    </div>
  );
}

function RawMimeTab({
  baseUrl,
  namespaceId,
  messageId,
}: {
  baseUrl: string;
  namespaceId: string;
  messageId: string;
}): React.ReactElement {
  const [result, setResult] = useState<FetchRawMimeResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    void (async () => {
      try {
        const r = await fetchRawMime(baseUrl, namespaceId, messageId);
        if (!cancelled) setResult(r);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load raw MIME");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baseUrl, namespaceId, messageId]);

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (error) {
    return (
      <p data-testid="raw-mime-error" className="text-sm text-destructive">
        Failed to load raw MIME: {error}
      </p>
    );
  }
  if (!result || result.status === "not-found") {
    return (
      <p data-testid="raw-mime-unavailable" className="text-sm text-muted-foreground">
        Raw MIME is not available. Objects are lifecycled out of S3 after ~25 hours.
      </p>
    );
  }
  if (result.status === "forbidden") {
    return (
      <p data-testid="raw-mime-forbidden" className="text-sm text-destructive">
        Forbidden — this message belongs to a different namespace.
      </p>
    );
  }
  return (
    <pre
      data-testid="raw-mime-body"
      className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-4 font-mono text-xs"
    >
      {result.body}
    </pre>
  );
}

function BodyTabs({
  baseUrl,
  namespaceId,
  message,
}: {
  baseUrl: string;
  namespaceId: string;
  message: Message;
}): React.ReactElement {
  return (
    <Tabs defaultValue="html" className="space-y-3">
      <div className="overflow-x-auto">
        <TabsList>
          <TabsTrigger value="html">HTML</TabsTrigger>
          <TabsTrigger value="text">Plain text</TabsTrigger>
          <TabsTrigger value="raw">Raw MIME</TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="html">
        {message.html?.body ? (
          <MessageHtmlFrame body={message.html.body} />
        ) : (
          <p className="text-sm text-muted-foreground">No HTML body.</p>
        )}
      </TabsContent>

      <TabsContent value="text">
        {message.text?.body ? (
          <pre
            data-testid="plain-text-body"
            className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-4 text-sm"
          >
            {message.text.body}
          </pre>
        ) : (
          <p className="text-sm text-muted-foreground">No plain-text body.</p>
        )}
      </TabsContent>

      <TabsContent value="raw">
        <RawMimeTab baseUrl={baseUrl} namespaceId={namespaceId} messageId={message.id} />
      </TabsContent>
    </Tabs>
  );
}

function ExtractedPanel({ message }: { message: Message }): React.ReactElement | null {
  const codes = Array.from(
    new Set([...(message.text?.codes ?? []), ...(message.html?.codes ?? [])])
  );
  const links = Array.from(
    new Set([...(message.text?.links ?? []), ...(message.html?.links ?? [])])
  );

  if (codes.length === 0 && links.length === 0) return null;

  return (
    <div className="space-y-4 rounded-md border bg-card p-4">
      <h3 className="text-base font-semibold">Extracted</h3>

      {codes.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-medium text-muted-foreground">Codes</h4>
          <ul className="flex flex-wrap gap-2" data-testid="extracted-codes">
            {codes.map((code) => (
              <li
                key={code}
                className="inline-flex items-center gap-1 rounded-md border bg-muted/30 px-2 py-1 font-mono text-sm"
              >
                <span>{code}</span>
                <CopyButton value={code} testId={`copy-code-${code}`} label={`Copy code ${code}`} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {links.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-medium text-muted-foreground">Links</h4>
          <ul className="space-y-2" data-testid="extracted-links">
            {links.map((link) => (
              <li key={link} className="flex flex-wrap items-center gap-2">
                {isClickableLink(link) ? (
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`extracted-link-${link}`}
                    className="inline-flex items-center gap-1 break-all text-sm text-primary underline-offset-2 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    <span className="break-all">{link}</span>
                  </a>
                ) : (
                  <span
                    data-testid={`extracted-link-unsafe-${link}`}
                    title="Non-http(s) scheme — not clickable from ops-console"
                    className="inline-flex items-center gap-1 break-all text-sm text-muted-foreground"
                  >
                    <AlertCircle className="h-3 w-3 shrink-0" />
                    <span className="break-all">{link}</span>
                  </span>
                )}
                <CopyButton value={link} testId={`copy-link-${link}`} label={`Copy link ${link}`} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

interface MessageDetailViewProps {
  baseUrl: string;
  namespaceId: string;
  messageId: string;
  onClose: () => void;
}

export function MessageDetailView({
  baseUrl,
  namespaceId,
  messageId,
  onClose,
}: MessageDetailViewProps): React.ReactElement {
  const { status, message, error } = useMessageDetail(baseUrl, namespaceId, messageId);

  return (
    <div data-testid="message-detail-view" className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="break-all font-mono text-xs text-muted-foreground" data-testid="detail-id">
          {messageId}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClose}
          aria-label="Close message detail"
          data-testid="detail-close"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {status === "loading" && (
        <div className="space-y-3" data-testid="message-loading">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {status === "not-found" && (
        <div data-testid="message-not-found" className="rounded-md border bg-card p-6">
          <h3 className="text-lg font-semibold">Message not found</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            This message was not found or expired. Raw MIME objects are deleted after ~25 hours.
          </p>
        </div>
      )}

      {status === "forbidden" && (
        <div data-testid="message-forbidden" className="rounded-md border bg-card p-6">
          <h3 className="text-lg font-semibold">Forbidden</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            This message belongs to a different namespace.
          </p>
        </div>
      )}

      {status === "error" && (
        <div data-testid="message-error" className="rounded-md border bg-card p-6">
          <h3 className="text-lg font-semibold">Failed to load message</h3>
          <p className="mt-2 text-sm text-muted-foreground">{error ?? "Unknown error"}</p>
        </div>
      )}

      {status === "ok" && message && (
        <>
          <HeaderPanel message={message} />
          <BodyTabs baseUrl={baseUrl} namespaceId={namespaceId} message={message} />
          <ExtractedPanel message={message} />
        </>
      )}
    </div>
  );
}
